import React, { useState } from "react";
import { useData } from "../../../context/DataContext";
import ImageUploadField from "../../../utils/ImageUploadField";
import { confirmDelete, successToast, errorToast } from "../../../utils/dialog";

const MealTypeAdmin = () => {
  const { mealTypes, addMealType, updateMealType, deleteMealType } = useData();

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    image: "",
  });
  const [editingId, setEditingId] = useState(null);
  const [imageFile, setImageFile] = useState(null); // Store the selected image file

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const previewUrl = URL.createObjectURL(file);
      setFormData((prev) => ({ ...prev, image: previewUrl })); // Show preview
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingId) {
        await updateMealType(editingId, formData, imageFile);
        successToast("Meal type updated.");
      } else {
        await addMealType(formData, imageFile);
        successToast("Meal type added.");
      }
      resetForm();
    } catch (err) {
      console.error(err);
      errorToast("Error saving meal type data.");
    }
  };

  const handleEdit = (meal) => {
    setFormData(meal);
    setEditingId(meal.id);
    setImageFile(null); // Reset the image file
  };

  const handleDelete = async (id) => {
    if (
      await confirmDelete({
        title: "Delete meal type?",
        text: "This meal type will be permanently removed.",
      })
    ) {
      await deleteMealType(id);
      successToast("Meal type deleted.");
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      image: "",
    });
    setEditingId(null);
    setImageFile(null);
  };

  return (
    <div className="p-6 space-y-8">
      <h2 className="text-3xl font-bold mb-4 text-gray-800">Meal Types Admin Panel</h2>

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow space-y-4">
        <h3 className="text-2xl font-semibold text-gray-700 mb-4">
          {editingId ? "Edit Meal Type" : "Add Meal Type"}
        </h3>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Title
          </label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            className="w-full p-2 border rounded focus:ring-2 focus:ring-[#D48C1E]"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            rows={3}
            className="w-full p-2 border rounded focus:ring-2 focus:ring-[#D48C1E]"
          />
        </div>
        <ImageUploadField
          label="Image"
          hint="Recommended size: 600×400px"
          previewUrl={formData.image}
          file={imageFile}
          onChange={handleFileChange}
          onClear={() => {
            setImageFile(null);
            setFormData((prev) => ({ ...prev, image: "" }));
          }}
        />
        <div className="flex justify-end space-x-4">
          <button
            type="submit"
            className="px-6 py-2 bg-[#14251F] hover:bg-[#1f3a30] text-[#FFD1A0] rounded transition font-medium"
          >
            {editingId ? "Update Meal Type" : "Add Meal Type"}
          </button>
          {editingId && (
            <button
              type="button"
              onClick={resetForm}
              className="px-6 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded transition"
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      {/* List of Meal Types */}
      <div className="bg-white p-6 rounded-lg shadow space-y-4">
        <h3 className="text-2xl font-semibold text-gray-700 mb-4">Existing Meal Types</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {mealTypes.map((meal) => (
            <div key={meal.id} className="border rounded p-4 shadow">
              <img
                src={meal.image || "https://via.placeholder.com/150"}
                alt={meal.title}
                className="w-full h-40 object-cover rounded mb-4"
              />
              <h4 className="text-lg font-bold">{meal.title}</h4>
              <p className="text-sm text-gray-600">{meal.description}</p>
              <div className="flex justify-end space-x-2 mt-4">
                <button
                  onClick={() => handleEdit(meal)}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(meal.id)}
                  className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MealTypeAdmin;